import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { createSupabaseBrowserClient } from "@/lib/supabase/client";
import { safePostLoginPath } from "@/lib/safe-post-login-path";
import { useSession } from "@/session/session-context";

export function SignInForm() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [searchParams] = useSearchParams();
  const afterSignInPath = safePostLoginPath(searchParams.get("next"));

  const [mode, setMode] = useState<"signin" | "reset">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  async function submitSignIn(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const sb = createSupabaseBrowserClient();
      const { error } = await sb.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) {
        setStatus("error");
        setMessage(error.message === "Invalid login credentials" ? "Invalid email or password." : error.message);
        return;
      }
      await refresh();
      navigate(afterSignInPath, { replace: true });
    } catch {
      setStatus("error");
      setMessage("Network error. Try again.");
    }
  }

  async function submitReset(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const raw = import.meta.env.NEXT_PUBLIC_SITE_URL?.trim() || window.location.origin;
      const origin = raw.replace(/\/$/, "");
      const redirectTo = `${origin}/auth/callback?next=${encodeURIComponent("/sign-in")}`;
      const sb = createSupabaseBrowserClient();
      const { error } = await sb.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
      if (error) {
        setStatus("error");
        setMessage(error.message);
        return;
      }
      setStatus("done");
      setMessage("If an account exists for that email, we sent a link to reset your password.");
    } catch {
      setStatus("error");
      setMessage("Network error. Try again.");
    }
  }

  if (mode === "reset") {
    return (
      <form onSubmit={(e) => void submitReset(e)} className="mt-8 space-y-4">
        <p className="text-sm text-zinc-600">
          Enter the email you used for your account. We’ll send a link to choose a new password.
        </p>
        <div>
          <label htmlFor="reset-email" className="block text-sm font-semibold text-zinc-800">
            Email
          </label>
          <input
            id="reset-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 min-h-12 w-full rounded-xl border border-zinc-200 bg-white px-3 py-3 text-sm outline-none ring-violet-400 focus:ring-2"
            placeholder="you@example.com"
            required
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="min-h-12 w-full rounded-xl bg-violet-600 px-4 text-sm font-bold text-white shadow-md transition hover:bg-violet-700 disabled:opacity-60"
        >
          {status === "loading" ? "Sending…" : "Send reset link"}
        </button>
        <button
          type="button"
          className="text-sm font-semibold text-violet-700 underline-offset-2 hover:underline"
          onClick={() => {
            setMode("signin");
            setStatus("idle");
            setMessage("");
          }}
        >
          Back to sign in
        </button>
        {message ? (
          <p className={`text-sm font-medium ${status === "error" ? "text-rose-600" : "text-emerald-700"}`}>{message}</p>
        ) : null}
      </form>
    );
  }

  return (
    <form onSubmit={(e) => void submitSignIn(e)} className="mt-8 space-y-4">
      <div>
        <label htmlFor="signin-email" className="block text-sm font-semibold text-zinc-800">
          Email
        </label>
        <input
          id="signin-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1.5 min-h-12 w-full rounded-xl border border-zinc-200 bg-white px-3 py-3 text-sm outline-none ring-violet-400 focus:ring-2"
          placeholder="you@example.com"
          required
        />
      </div>
      <div>
        <label htmlFor="signin-password" className="block text-sm font-semibold text-zinc-800">
          Password
        </label>
        <input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 min-h-12 w-full rounded-xl border border-zinc-200 bg-white px-3 py-3 text-sm outline-none ring-violet-400 focus:ring-2"
          required
        />
      </div>
      <button
        type="submit"
        disabled={status === "loading"}
        className="min-h-12 w-full rounded-xl bg-violet-600 px-4 text-sm font-bold text-white shadow-md transition hover:bg-violet-700 disabled:opacity-60"
      >
        {status === "loading" ? "Signing in…" : "Sign in"}
      </button>
      <div className="flex justify-between gap-3 text-sm">
        <button
          type="button"
          className="font-semibold text-violet-700 underline-offset-2 hover:underline"
          onClick={() => {
            setMode("reset");
            setStatus("idle");
            setMessage("");
          }}
        >
          Forgot password?
        </button>
      </div>
      {message ? (
        <p className={`text-sm font-medium ${status === "error" ? "text-rose-600" : "text-emerald-700"}`}>{message}</p>
      ) : null}
    </form>
  );
}
